import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { deliveryNoteService, invoiceService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';

const DetailContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 1rem;
  font-size: 0.9rem;

  &:hover {
    background-color: #5a6268;
  }

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const PrintLogo = styled.div`
  display: none;

  @media print {
    display: block;
    margin-bottom: 1rem;

    img {
      width: 200px;
      height: auto;
    }
  }
`;

const PrintHeaderWrapper = styled.div`
  display: none;

  @media print {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #999;
  }
`;

const CompanyInfo = styled.div`
  text-align: left;

  h2 {
    margin: 0 0 0.25rem 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: #333;
  }

  p {
    margin: 0.1rem 0;
    font-size: 0.9rem;
    color: #666;
  }
`;

const PrintRightColumn = styled.div`
  text-align: right;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 2rem;
  }

  @media print {
    display: none;
  }
`;

const Info = styled.div`
  @media print {
    text-align: right;
  }
`;

const Number = styled.h1`
  color: #333;
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Date = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$status) {
      case 'draft': return '#6c757d20';
      case 'confirmed': return '#ffc10720';
      case 'delivered': return '#4CAF5020';
      case 'partial_invoiced': return '#ff980020';
      case 'invoiced': return '#28a74520';
      case 'cancelled': return '#dc354520';
      default: return '#6c757d20';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'draft': return '#6c757d';
      case 'confirmed': return '#ffc107';
      case 'delivered': return '#4CAF50';
      case 'partial_invoiced': return '#ff9800';
      case 'invoiced': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  }};

  @media print {
    display: none;
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const ClientSection = styled.div`
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }

  @media print {
    display: none;
  }
`;

const SectionTitle = styled.h2`
  color: #555;
  font-size: 1rem;
  margin-bottom: 0.75rem;

  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  @media print {
    display: none;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media print {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }
`;

const InfoItem = styled.div`
  padding: 0.25rem 0;

  @media (min-width: 768px) {
    padding: 0.5rem 0;
  }

  @media print {
    text-align: right;
    padding: 0.1rem 0;
  }
`;

const InfoLabel = styled.span`
  color: #666;
  font-weight: 500;
  margin-right: 0.5rem;
  font-size: 0.9rem;

  @media print {
    display: none;
  }
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 0.9rem;
`;

const ItemsTable = styled.table`
  display: none;
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  table-layout: fixed;

  thead {
    border-bottom: 1px solid #999;
  }

  @media (min-width: 768px) {
    display: table;
  }

  @media print {
    display: table;
    line-height: 1.2;

    thead {
      border-bottom: 1px solid #999 !important;
    }
  }
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #555;

  @media print {
    background-color: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding: 0.4rem 0.5rem;

    &:first-child {
      padding-left: 0;
    }
  }
`;

const TableHeaderCentered = styled(TableHeader)`
  text-align: center !important;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;

  @media print {
    padding: 0.4rem 0.5rem;
    line-height: 1.2;

    &:first-child {
      padding-left: 0;
    }
  }
`;

// Mobile Card Components for Items
const MobileItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

const MobileItemCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid ${props => props.$invoiced ? '#28a745' : '#2196F3'};
`;

const MobileItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const MobileItemTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
  flex: 1;
`;

const MobileItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const MobileItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
`;

const MobileItemLabel = styled.span`
  color: #666;
`;

const MobileItemValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const MobileCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e0e0e0;
`;

const InvoicedBadge = styled.span`
  background-color: #28a74520;
  color: #28a745;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
`;

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.75rem 1rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 0;
  font-size: ${props => props.$isTotal ? '1.2rem' : '1rem'};
  font-weight: 700;
  color: ${props => props.$isTotal ? '#333' : '#666'};
`;

const NotesSection = styled.div`
  margin-top: 1rem;
  padding: 0.5rem 0.75rem;
  background-color: #f8f9fa;
  border-radius: 3px;
  border-left: 2px solid #6c757d;
`;

const NotesTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6c757d;
  margin: 0 0 0.25rem 0;
`;

const NotesContent = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.875rem;
  white-space: pre-line;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    margin-top: 2rem;
  }

  @media print {
    display: none;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1rem;
  background-color: ${props => props.$variant === 'primary' ? '#4CAF50' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;

  &:hover {
    background-color: ${props => props.$variant === 'primary' ? '#45a049' : '#5a6268'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    width: auto;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  margin-right: 0.5rem;

  @media print {
    display: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 1.5rem;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
  }
`;

const ModalTitle = styled.h2`
  color: #333;
  margin: 0 0 1rem 0;
  font-size: 1.25rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalMessage = styled.p`
  color: #666;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
  font-size: 0.95rem;

  @media (min-width: 768px) {
    margin: 0 0 2rem 0;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
    justify-content: flex-end;
  }
`;

const DangerButton = styled(Button)`
  background-color: #dc3545;

  &:hover {
    background-color: #c82333;
  }
`;

const formatDeliveryStatus = (status) => {
  const statusMap = {
    draft: 'Borrador',
    confirmed: 'Confirmado',
    delivered: 'Entregado',
    partial_invoiced: 'Parcialmente Facturado',
    invoiced: 'Facturado',
    cancelled: 'Cancelado'
  };
  return statusMap[status] || status;
};

const DeliveryNoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLines, setSelectedLines] = useState([]);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showPriceColumns, setShowPriceColumns] = useState(true);

  // Helper function to round to 5 cents (for TPV orders)
  const roundToFiveCents = (number) => {
    return Math.ceil(number * 20) / 20;
  };

  // Helper to determine if an order was created with Calculadora Profesional
  const isCalculadoraPro = (fingerprint) => {
    if (!fingerprint) return false;
    // CalculatoraPro orders have fingerprint starting with INT- or EXT-
    return fingerprint.startsWith('INT-') || fingerprint.startsWith('EXT-');
  };

  // Helper to calculate totals with correct rounding based on delivery note type
  const calculateDisplayTotals = (deliveryNote) => {
    if (!deliveryNote) return { base: 0, iva: 0, total: 0 };
    const finalAmount = parseFloat(deliveryNote.totalAmount || 0);

    // Check if ALL lines are from CalculadoraPro
    const allLinesAreCalculadoraPro = deliveryNote.lines?.every(line =>
      line.orderSnapshot?.fingerprint && isCalculadoraPro(line.orderSnapshot.fingerprint)
    );

    if (allLinesAreCalculadoraPro) {
      // CalculadoraPro orders - round BASE IMPONIBLE to 5 cents, then calculate VAT
      const baseImponible = finalAmount / 1.21;
      const baseRedondeada = roundToFiveCents(baseImponible);
      const iva = baseRedondeada * 0.21;
      const total = baseRedondeada + iva;
      return { base: baseRedondeada, iva, total };
    } else {
      // Has TPV orders - round TOTAL (with VAT) to 5 cents, then reverse calculate base
      const totalRedondeado = roundToFiveCents(finalAmount);
      const baseImponible = totalRedondeado / 1.21;
      const iva = totalRedondeado - baseImponible;
      return { base: baseImponible, iva, total: totalRedondeado };
    }
  };

  const { data: rawDeliveryNote, isLoading, error } = useQuery({
    queryKey: ['deliveryNote', id],
    queryFn: () => deliveryNoteService.getById(id),
  });

  // Adjust delivery note lines to match totalAmount (for rounding)
  const deliveryNote = React.useMemo(() => {
    if (!rawDeliveryNote) return null;

    // Calculate correct base amount using same logic as display
    const { base: baseAmountWithoutVAT } = calculateDisplayTotals(rawDeliveryNote);
    const linesTotal = rawDeliveryNote.lines?.reduce((sum, line) => sum + parseFloat(line.totalPrice || 0), 0) || 0;
    const difference = baseAmountWithoutVAT - linesTotal;

    // If there's a small rounding difference, adjust the last line
    if (Math.abs(difference) > 0.001 && Math.abs(difference) < 1.0 && rawDeliveryNote.lines && rawDeliveryNote.lines.length > 0) {
      const adjustedLines = [...rawDeliveryNote.lines];
      const lastLine = adjustedLines[adjustedLines.length - 1];
      const adjustedTotalPrice = parseFloat(lastLine.totalPrice) + difference;

      adjustedLines[adjustedLines.length - 1] = {
        ...lastLine,
        totalPrice: adjustedTotalPrice,
        unitPrice: lastLine.quantity > 0 ? adjustedTotalPrice / lastLine.quantity : adjustedTotalPrice
      };

      return {
        ...rawDeliveryNote,
        lines: adjustedLines
      };
    }

    return rawDeliveryNote;
  }, [rawDeliveryNote]);

  const updateStatusMutation = useMutation({
    mutationFn: (status) => deliveryNoteService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['deliveryNote', id]);
      queryClient.invalidateQueries(['deliveryNotes']);
      toast.success('Estado actualizado');
    },
    onError: (error) => {
      console.error('Delivery note status update error', error.response?.data || error);
      const message = error.response?.data?.details || error.response?.data?.error || 'Error al actualizar estado';
      toast.error(message);
    }
  });

  const revertMutation = useMutation({
    mutationFn: () => deliveryNoteService.revert(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['deliveryNote', id]);
      queryClient.invalidateQueries(['deliveryNotes']);
      queryClient.invalidateQueries(['orders']);
      toast.success(data.message || 'Albarán revertido correctamente');
      setShowRevertModal(false);
      navigate('/delivery-notes');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al revertir el albarán');
      setShowRevertModal(false);
    }
  });

  // Handle error with useEffect - MUST be before any conditional returns
  React.useEffect(() => {
    if (error && !isLoading) {
      toast.error('Error al cargar el albarán');
      navigate('/delivery-notes');
    }
  }, [error, isLoading, navigate]);

  const handleSelectAll = (e) => {
    if (e.target.checked && deliveryNote?.lines) {
      const uninvoicedLines = deliveryNote.lines
        .filter(line => !line.isInvoiced)
        .map(line => line.id);
      setSelectedLines(uninvoicedLines);
    } else {
      setSelectedLines([]);
    }
  };

  const handleSelectLine = (lineId) => {
    setSelectedLines(prev =>
      prev.includes(lineId)
        ? prev.filter(id => id !== lineId)
        : [...prev, lineId]
    );
  };

  const handleCreateInvoice = async () => {
    if (selectedLines.length === 0) {
      toast.warning('Seleccione al menos una línea para facturar');
      return;
    }

    try {
      // Call the API to create invoice from selected delivery lines
      const response = await invoiceService.createFromDeliveryLines(selectedLines);

      toast.success(`Factura generada correctamente: FAC${response.invoice?.invoiceNumber || ''}`);

      // Refresh the delivery note data
      queryClient.invalidateQueries(['deliveryNote', id]);
      queryClient.invalidateQueries(['deliveryNotes']);

      // Clear selection
      setSelectedLines([]);

    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.error || 'Error al generar la factura');
    }
  };

  const downloadPDF = async ({ showPrices }) => {
    try {
      const pdf = await deliveryNoteService.getPDF(id, { showPrices });
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `albaran-ALB${deliveryNote.deliveryNumber}${showPrices ? '' : '-sin-precios'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading delivery note PDF:', error);
      toast.error('Error al descargar el PDF');
    }
  };

  const handlePrintWithPrices = async () => {
    setShowPriceColumns(true);
    await downloadPDF({ showPrices: true });
  };

  const handlePrintWithoutPrices = async () => {
    setShowPriceColumns(false);
    await downloadPDF({ showPrices: false });
  };

  const handleRevert = () => {
    setShowRevertModal(true);
  };

  const confirmRevert = () => {
    revertMutation.mutate();
  };

  if (isLoading) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
          Cargando albarán...
        </div>
      </DetailContainer>
    );
  }

  if (error || !deliveryNote) {
    return null;
  }

  const hasUninvoicedLines = deliveryNote.lines?.some(line => !line.isInvoiced);
  const canInvoice = ['confirmed', 'delivered', 'partial_invoiced'].includes(deliveryNote.status);

  return (
    <DetailContainer>
      {/* Logo solo para impresión - fuera del Card */}
      <PrintLogo>
        <img src="https://enmarcacion-iltzar.com/logo_itzar.jpg" alt="Iltzar" />
      </PrintLogo>

      <Card>
        {/* Cabecera solo para impresión */}
        <PrintHeaderWrapper>
          <CompanyInfo>
            <p>ILTZAR S.L.U.</p>
            <p>B-95040705</p>
            <p>94 444 25 82</p>
            <p>C\Garcia Salazar 8</p>
            <p>BILBAO 48003</p>
          </CompanyInfo>
          <PrintRightColumn>
            <div style={{ fontWeight: '700', fontSize: '1.4rem', marginBottom: '0.25rem' }}>
              Albarán: ALB{deliveryNote.deliveryNumber}
            </div>
            <div style={{ fontWeight: '700',fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              {formatDate(deliveryNote.deliveryDate)}
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              {deliveryNote.clientSnapshot?.name}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {deliveryNote.clientSnapshot?.taxId}
            </div>
            {deliveryNote.clientSnapshot?.address && (
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                {deliveryNote.clientSnapshot.address}
              </div>
            )}
          </PrintRightColumn>
        </PrintHeaderWrapper>

        <Header>
          <Info>
            <Number>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Albarán ALB{deliveryNote.deliveryNumber}
                {deliveryNote.isImportant && (
                  <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#dc3545',
                    borderRadius: '50%',
                    boxShadow: '0 0 8px rgba(220, 53, 69, 0.6)'
                  }}
                  title="Albarán urgente"
                  />
                )}
              </span>
            </Number>
            <Date>{formatDate(deliveryNote.deliveryDate)}</Date>
          </Info>
          <StatusBadge $status={deliveryNote.status}>
            {formatDeliveryStatus(deliveryNote.status)}
          </StatusBadge>
        </Header>

        <ClientSection>
          <SectionTitle>Información del Cliente</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Cliente:</InfoLabel>
              <InfoValue>{deliveryNote.clientSnapshot?.name || 'Sin cliente'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>NIF/CIF:</InfoLabel>
              <InfoValue>{deliveryNote.clientSnapshot?.taxId || 'No especificado'}</InfoValue>
            </InfoItem>
          </InfoGrid>
          {deliveryNote.clientSnapshot?.address && (
            <InfoItem>
              <InfoLabel>Dirección:</InfoLabel>
              <InfoValue>{deliveryNote.clientSnapshot.address}</InfoValue>
            </InfoItem>
          )}
          {deliveryNote.deliveryAddress && (
            <InfoItem>
              <InfoLabel>Dirección de entrega:</InfoLabel>
              <InfoValue>{deliveryNote.deliveryAddress}</InfoValue>
            </InfoItem>
          )}
        </ClientSection>

        <Section>
          <SectionTitle>
            {canInvoice && hasUninvoicedLines && (
              <Checkbox
                type="checkbox"
                checked={selectedLines.length === deliveryNote.lines.filter(l => !l.isInvoiced).length}
                onChange={handleSelectAll}
                style={{ marginLeft: '1rem' }}
              />
            )}
          </SectionTitle>
          <ItemsTable>
            <thead>
              <tr>
                {canInvoice && <TableHeader style={{ width: '50px' }} rowSpan={showPriceColumns ? 2 : 1}></TableHeader>}
                <TableHeader rowSpan={showPriceColumns ? 2 : 1} style={{ textAlign: 'left', width: '50%' }}>DESCRIPCIÓN</TableHeader>
                <TableHeader rowSpan={showPriceColumns ? 2 : 1} style={{ textAlign: 'center', width: '10%' }}>UDS</TableHeader>
                {showPriceColumns && <TableHeaderCentered colSpan={2} style={{ width: '40%' }}>IMPORTE TOTAL</TableHeaderCentered>}
              </tr>
            </thead>
            <tbody>
              {deliveryNote.lines?.map((line) => (
                <tr key={line.id}>
                  {canInvoice && (
                    <TableCell>
                      {!line.isInvoiced && (
                        <Checkbox
                          type="checkbox"
                          checked={selectedLines.includes(line.id)}
                          onChange={() => handleSelectLine(line.id)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell style={{ textAlign: 'left' }}>
                    {/* Si es Leroy Merlin y tiene códigos, mostrar códigos */}
                    {deliveryNote.clientSnapshot?.name?.toUpperCase().includes('LEROY MERLIN') &&
                     line.orderSnapshot?.clientReference && line.orderSnapshot?.supplierReference ? (
                      <div style={{ fontWeight: '500' }}>
                        {line.orderSnapshot.clientReference} - {line.orderSnapshot.supplierReference}
                      </div>
                    ) : (
                      <>
                        <div style={{ fontWeight: '500' }}>{line.description}</div>
                        {line.orderSnapshot?.measurementInfo && (
                          <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '2px' }}>
                            {line.orderSnapshot.measurementInfo.width &&
                             line.orderSnapshot.measurementInfo.height &&
                             `${line.orderSnapshot.measurementInfo.width}x${line.orderSnapshot.measurementInfo.height} cm`}
                          </div>
                        )}
                      </>
                    )}
                    {line.isInvoiced && (
                      <div style={{ marginTop: '4px' }}>
                        <InvoicedBadge>Facturado</InvoicedBadge>
                      </div>
                    )}
                  </TableCell>
                  <TableCell style={{ textAlign: 'center' }}>{line.quantity}</TableCell>
                  {showPriceColumns && <TableCell style={{ textAlign: 'right' }}>{formatCurrency(line.unitPrice)}</TableCell>}
                  {showPriceColumns && (
                    <TableCell style={{ textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(line.totalPrice)}
                    </TableCell>
                  )}
                </tr>
              ))}
            </tbody>
          </ItemsTable>

          {/* Mobile Card View for Items */}
          <MobileItemsList>
            {deliveryNote.lines?.map((line) => (
              <MobileItemCard key={line.id} $invoiced={line.isInvoiced}>
                <MobileItemHeader>
                  <MobileItemTitle>
                    {deliveryNote.clientSnapshot?.name?.toUpperCase().includes('LEROY MERLIN') &&
                     line.orderSnapshot?.clientReference && line.orderSnapshot?.supplierReference
                      ? `${line.orderSnapshot.clientReference} - ${line.orderSnapshot.supplierReference}`
                      : line.description}
                    {line.isInvoiced && <InvoicedBadge>Facturado</InvoicedBadge>}
                  </MobileItemTitle>
                </MobileItemHeader>
                <MobileItemBody>
                  <MobileItemRow>
                    <MobileItemLabel>Pedido</MobileItemLabel>
                    <MobileItemValue>#{line.orderSnapshot?.orderNumber}</MobileItemValue>
                  </MobileItemRow>
                  {line.orderSnapshot?.measurementInfo?.width && line.orderSnapshot?.measurementInfo?.height && (
                    <MobileItemRow>
                      <MobileItemLabel>Medidas</MobileItemLabel>
                      <MobileItemValue>
                        {line.orderSnapshot.measurementInfo.width}x{line.orderSnapshot.measurementInfo.height} cm
                      </MobileItemValue>
                    </MobileItemRow>
                  )}
                  <MobileItemRow>
                    <MobileItemLabel>Cantidad</MobileItemLabel>
                    <MobileItemValue>{line.quantity}</MobileItemValue>
                  </MobileItemRow>
                  {showPriceColumns && (
                    <>
                      <MobileItemRow>
                        <MobileItemLabel>Precio Unit.</MobileItemLabel>
                        <MobileItemValue>{formatCurrency(line.unitPrice)}</MobileItemValue>
                      </MobileItemRow>
                      <MobileItemRow>
                        <MobileItemLabel>Total</MobileItemLabel>
                        <MobileItemValue>{formatCurrency(line.totalPrice)}</MobileItemValue>
                      </MobileItemRow>
                    </>
                  )}
                </MobileItemBody>
                {canInvoice && !line.isInvoiced && (
                  <MobileCheckboxRow>
                    <Checkbox
                      type="checkbox"
                      checked={selectedLines.includes(line.id)}
                      onChange={() => handleSelectLine(line.id)}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Seleccionar para facturar</span>
                  </MobileCheckboxRow>
                )}
              </MobileItemCard>
            ))}
          </MobileItemsList>
        </Section>

        {showPriceColumns && (() => {
          // Apply conditional rounding based on order type
          // CalculadoraPro: round BASE then add VAT
          // TPV: round TOTAL then reverse calculate base
          const { base, iva, total } = calculateDisplayTotals(deliveryNote);
          return (
            <TotalSection>
              <TotalRow>
                <span>Base Imponible:</span>
                <span>{formatCurrency(base)}</span>
              </TotalRow>
              <TotalRow>
                <span>IVA (21%):</span>
                <span>{formatCurrency(iva)}</span>
              </TotalRow>
              <TotalRow $isTotal>
                <span>Total:</span>
                <span>{formatCurrency(total)}</span>
              </TotalRow>
            </TotalSection>
          );
        })()}

        {deliveryNote.notes && deliveryNote.notes.trim() && (
          <NotesSection>
            <NotesTitle>Observaciones</NotesTitle>
            <NotesContent>{deliveryNote.notes}</NotesContent>
          </NotesSection>
        )}

        <ButtonGroup>
          <Button $variant="secondary" onClick={handlePrintWithPrices}>
            Imprimir con Precios
          </Button>

          <Button $variant="secondary" onClick={handlePrintWithoutPrices}>
            Imprimir sin Precios
          </Button>

          {deliveryNote.status === 'draft' && (
            <Button
              $variant="primary"
              onClick={() => updateStatusMutation.mutate('confirmed')}
            >
              Confirmar Albarán
            </Button>
          )}

          {deliveryNote.status === 'confirmed' && (
            <Button
              $variant="primary"
              onClick={() => updateStatusMutation.mutate('delivered')}
            >
              Marcar como Entregado
            </Button>
          )}

          {canInvoice && hasUninvoicedLines && (
            <Button
              $variant="primary"
              onClick={handleCreateInvoice}
              disabled={selectedLines.length === 0}
            >
              Facturar Seleccionados
            </Button>
          )}

          {deliveryNote.status !== 'invoiced' && deliveryNote.status !== 'partial_invoiced' && deliveryNote.status !== 'cancelled' && (
            <DangerButton onClick={handleRevert}>
              Revertir Albarán
            </DangerButton>
          )}
        </ButtonGroup>
      </Card>

      {showRevertModal && (
        <ModalOverlay onClick={() => setShowRevertModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>¿Revertir albarán?</ModalTitle>
            <ModalMessage>
              Esta acción cancelará el albarán y devolverá todos los pedidos asociados al estado "Listo".
              <br /><br />
              ¿Estás seguro de que deseas continuar?
            </ModalMessage>
            <ModalButtons>
              <Button
                $variant="secondary"
                onClick={() => setShowRevertModal(false)}
                disabled={revertMutation.isLoading}
              >
                Cancelar
              </Button>
              <DangerButton
                onClick={confirmRevert}
                disabled={revertMutation.isLoading}
              >
                {revertMutation.isLoading ? 'Revirtiendo...' : 'Sí, Revertir'}
              </DangerButton>
            </ModalButtons>
          </Modal>
        </ModalOverlay>
      )}
    </DetailContainer>
  );
};

export default DeliveryNoteDetail;
